import { startCase } from 'lodash';
import { DynamicRule, RuleEntity, RuleOperator } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { groupRules } from '@app/sharedModule/change-review/rule-groups';
import { RulesEntitiesToDisplayNameMap, RulesEntityOperatorToDisplayNameMap } from './model';

const TIME_OF_DAY_LABELS: Record<string, string> = {
  morning: 'Morning (06:00-11:59 AM)',
  noon: 'Noon (12:00-05:59 PM)',
  evening: 'Evening (06:00-11:59 PM)',
  night: 'Night (12:00-05:59 AM)',
};

export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// rules like "Arrival date is more than 5 calendar days away" spell their words out in these params
const SENTENCE_PARAMS = ['prefix', 'middle', 'suffix'];

export interface RuleFormatOptions {
  money?: (value: unknown) => string;
  // a readable value for params that hold ids, e.g. location ids → names
  paramLabel?: (param: string, value: unknown) => string | undefined;
}

// rules without a groupIndex come last
export const dynamicRuleGroup = (rule: DynamicRule): number => Number(rule.groupIndex ?? Number.MAX_SAFE_INTEGER);

// one list of rule sentences per group
export function formatExtensionConditions(rules: DynamicRule[]): string[][] {
  return groupRules(rules ?? [], dynamicRuleGroup).map((group) => group.map((rule) => describeDynamicRule(rule)));
}

export function describeDynamicRule(rule: DynamicRule, options: RuleFormatOptions = {}): string {
  const entity = RulesEntitiesToDisplayNameMap[rule.entity] || startCase(String(rule.entity ?? ''));
  const params = rule.entityParams ?? {};
  if (SENTENCE_PARAMS.some((param) => param in params)) {
    const words = Object.entries(params).map(
      ([param, value]) => options.paramLabel?.(param, value) ?? formatParamValue(value),
    );
    return [entity, ...words].filter(Boolean).join(' ');
  }
  return [entity, formatOperator(rule.entityOperator), formatRuleValue(rule, options)].filter(Boolean).join(' ');
}

export function formatOperator(operator: RuleOperator | undefined): string {
  if (!operator) return '';
  return RulesEntityOperatorToDisplayNameMap[operator] || operator.replace(/_/g, ' ');
}

export function formatRuleValue(rule: DynamicRule, { money = String }: RuleFormatOptions = {}): string {
  const params = rule.entityParams || {};

  switch (rule.entity) {
    case RuleEntity.DELIVERY_AREA_DESTINATIONS: {
      const countries = Object.keys(params.deliveryAreaDestinationsValue || {});
      return countries.length > 0 ? countries.join(', ') : 'No countries selected';
    }
    case RuleEntity.DELIVERY_AREA_ZIP_CODES:
      return String(params.zipCodeValue || '');
    case RuleEntity.CART_VALUE:
      return money(params.cartValue || 0);
    case RuleEntity.CUSTOMER_ORDER_COUNT: {
      const count = params.customerOrderCountValue || 0;
      const periodType = params.customerOrderCountTimePeriodType || '';
      const periodValue = params.customerOrderCountTimePeriodValue || '';
      if (periodType === 'last_days' && periodValue) return `${count} in the last ${periodValue} days`;
      return periodType === 'all_time' ? `${count} (all time)` : String(count);
    }
    case RuleEntity.WEEKDAY_SEGMENTATION: {
      const weekdays = params.weekdaysValue || [];
      return Array.isArray(weekdays) && weekdays.length > 0
        ? weekdays.map((day) => WEEKDAYS[Number(day)] || day).join(', ')
        : 'weekdays';
    }
    case RuleEntity.TIME_OF_DAY_SEGMENTATION: {
      const value = String(params.timeOfDayValue || '');
      return TIME_OF_DAY_LABELS[value] || value;
    }
    case RuleEntity.COUPONS:
      return String(params.couponTypeValue || '');
    case RuleEntity.HAS_DISCOUNT:
      return formatParamValue(params.hasDiscountValue ?? params.hasDiscount);
    case RuleEntity.HAS_SUBSCRIPTION_PRODUCT:
      return formatParamValue(params.hasSubscriptionProductValue ?? params.hasSubscriptionProduct);
  }

  const valueKey = Object.keys(params).find((key) => key.endsWith('Value'));
  return valueKey ? formatParamValue(params[valueKey]) : '';
}

function formatParamValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object' && value !== null) return Object.keys(value).join(', ');
  return value === undefined || value === null ? '' : String(value);
}
